import React, { useImperativeHandle, useState } from "react";
import debounce from "lodash/debounce";
import { useRouter } from "next/router";

import Tab from "components/botanic/Tab";
import Tabs from "components/botanic/Tabs";
import SearchIcon from "components/Icons/searchBar/search.svg";
import CloseIcon from "components/Icons/searchBar/x.svg";

export enum LocationFilter {
  ALL = "all",
  NEARBY = "nearby",
}

export type BrandListHeaderProps = {
  H1Text: string;
  searchQuery: string | null;
};

export interface HeaderComponentRef {
  setSearch: () => void;
}

const BrandListHeader = React.forwardRef<
  HeaderComponentRef,
  BrandListHeaderProps
>(({ H1Text, searchQuery = "" }: BrandListHeaderProps, ref) => {
  const [searchTerm, setSearchTerm] = useState(searchQuery || "");

  const router = useRouter();

  const isAllBrandsPage = router.asPath.includes("/all");

  // Expose the reset function to the parent using useImperativeHandle
  useImperativeHandle(ref, () => ({
    setSearch: () => setSearchTerm(""),
  }));

  const getURLForTab = (location: LocationFilter) => {
    return `/brands${location === LocationFilter.ALL ? "/all" : ""}${
      searchQuery ? `?search=${searchQuery}` : ""
    }`;
  };

  const search = debounce((event) => {
    // this will stop ?search= from being added to the query params
    if (!event.target.value && !searchTerm) {
      return;
    }
    router.push({
      query: {
        // This will add search to the query and trigger and search is there is a search value present
        // OR it will clear the search and return the default result set.
        ...(event.target.value ? { search: event.target.value } : {}),
      },
    });
  }, 300);

  return (
    <div className="container">
      <h1 className="mb-md">
        {searchTerm ? `Showing results for ${searchTerm}` : H1Text}
      </h1>
      <div className="relative mb-lg">
        <div className="absolute top-0 left-1.5 flex h-full text-default">
          <button className="px-lg" type="submit" aria-label="Search brands">
            <SearchIcon
              className="flex-shrink-0 block search__icon"
              height="16"
              width="16"
            />
          </button>
        </div>
        <input
          className="pl-10 p-sm bg-white"
          aria-label="Brand name search"
          data-testid="search-input"
          placeholder="Search brands"
          type="text"
          value={searchTerm}
          onChange={(e) => {
            search(e);
            setSearchTerm(e.target.value);
          }}
        />
        {searchTerm && (
          <div className="absolute top-0 right-0 flex h-full text-default">
            <button
              className="px-sm"
              type="button"
              aria-label="Clear search"
              data-testid="clear-search-icon"
              onClick={() => {
                setSearchTerm("");
                router.push({});
              }}
            >
              <CloseIcon
                className="flex-shrink-0 block search__icon"
                height="15"
                width="15"
              />
            </button>
          </div>
        )}
      </div>
      <Tabs className="mb-xl" ariaLabel="Brands page">
        <Tab
          active={!isAllBrandsPage}
          href={getURLForTab(LocationFilter.NEARBY)}
        >
          Available nearby
        </Tab>
        <Tab active={isAllBrandsPage} href={getURLForTab(LocationFilter.ALL)}>
          All brands
        </Tab>
      </Tabs>
    </div>
  );
});

export default BrandListHeader;
